import { useState, type FormEvent } from 'react';
import { Cabin } from '../components/Cabin';
import { Dialogue } from '../components/Dialogue';
import { Panel } from '../components/Panel';
import { PixelButton } from '../components/PixelButton';
import { Triangle } from '../components/Triangle';
import { api } from '../lib/api';

/** Pull the one-time token out of /reset?token=… */
function tokenFromUrl(): string {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('token') ?? '';
}

/**
 * Standalone "choose a new key" screen, reached from the emailed reset link.
 * Rendered by the Gate before the auth check, so it works while logged out.
 */
export function ResetScreen() {
  const [token] = useState(tokenFromUrl);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('your new key needs at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setError("those two keys don't match");
      return;
    }
    setPending(true);
    try {
      await api.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  };

  // A full navigation clears the ?token from the URL and re-runs the auth probe.
  const toDoor = () => { window.location.assign('/'); };

  return (
    <div className="auth-stage">
      <div className="auth-brand"><Cabin size={34} /><span>HABIT HOLLOW</span></div>
      <div className="auth-sub">a small 16-bit habit log</div>

      <Panel title="A NEW KEY" bodyClassName="panel-body auth-body">
        {!token ? (
          <>
            <Dialogue speaker="MAYOR" style={{ marginBottom: 18 }}>
              Hmm — this reset link looks worn clean through. Best ask for a fresh one back at the door.
            </Dialogue>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <PixelButton variant="gold" type="button" onClick={toDoor}>
                <Triangle /> TO THE DOOR
              </PixelButton>
            </div>
          </>
        ) : done ? (
          <>
            <Dialogue speaker="MAYOR" style={{ marginBottom: 18 }}>
              There we are — your new key is cut and ready. Off you pop and let yourself in.
            </Dialogue>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <PixelButton variant="gold" type="button" onClick={toDoor}>
                <Triangle /> LOG IN
              </PixelButton>
            </div>
          </>
        ) : (
          <>
            <Dialogue speaker="MAYOR" style={{ marginBottom: 18 }}>
              Right then — pick a new key for your ledger. Make it a good one.
            </Dialogue>
            <form onSubmit={submit} noValidate>
              <div className="field">
                <div className="field__label">
                  NEW PASSWORD <span style={{ opacity: 0.6 }}>· 8+ CHARS</span>
                </div>
                <input
                  className="pixel-input"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoFocus
                  required
                  minLength={8}
                />
              </div>
              <div className="field">
                <div className="field__label">CONFIRM PASSWORD</div>
                <input
                  className="pixel-input"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  required
                  minLength={8}
                />
              </div>

              {error && <div className="auth-error">▸ {error}</div>}

              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 14 }}>
                <PixelButton variant="ghost" type="button" onClick={toDoor}>
                  <Triangle direction="left" /> CANCEL
                </PixelButton>
                <PixelButton variant="gold" type="submit" disabled={pending}>
                  {pending ? 'SETTING…' : <><Triangle /> SET NEW KEY</>}
                </PixelButton>
              </div>
            </form>
          </>
        )}
      </Panel>

      <div className="auth-footnote">
        Your data is yours alone. No one else can see your quests.
      </div>
    </div>
  );
}
